/**
 * Org Import service — "Upload your org chart" AI import (n8n Workflow 7).
 *
 * Flow:
 *   1. startOrgImport(): upload the file to Storage, create the Hugo_OrgImports
 *      doc, fire the WF7 webhook. Status: 'uploading' → (WF7) 'parsing' →
 *      'ready_for_review' | 'failed'.
 *   2. The dialog subscribes to the import doc (subscribeToOrgImport).
 *   3. approveOrgImport(): the HUMAN approves the (possibly edited) structure —
 *      only then is the organization actually created. Insight, not autopilot.
 *
 * Document structure (Hugo_OrgImports/{importId}):
 * - fileName, contentType, storagePath, orgNameHint, requestedBy
 * - status: 'uploading' | 'parsing' | 'ready_for_review' | 'failed' | 'created'
 * - proposedStructure: { orgName, industry, sites[], departments[], teams[] } (WF7)
 * - confidence, ambiguities[], counts (WF7)
 * - userMessage, errorDetail (WF7, on failure)
 * - createdOrganizationId (after approval)
 */
import { db, storage } from '../firebase';
import { doc, setDoc, updateDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createOrganization, updateOrganization } from './organizationService';
import { triggerOrgImport } from './n8nService';
import { mergeIntoExisting, withSiteCodes, rosterFromPeople } from '../assistants/drew/utils/importStructure';

const COLLECTION_NAME = 'Hugo_OrgImports';
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/** Extensions we parse directly. pptx/vsdx are intercepted with an export hint. */
export const SUPPORTED_EXTENSIONS = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.csv': 'text/csv',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.txt': 'text/plain',
};

export const EXPORT_HINT_EXTENSIONS = ['.pptx', '.ppt', '.vsdx', '.vsd', '.key'];

export function validateImportFile(file) {
  const ext = ('.' + (file.name.split('.').pop() || '')).toLowerCase();
  if (EXPORT_HINT_EXTENSIONS.includes(ext)) {
    return {
      ok: false,
      reason: `We can't read ${ext} files directly — export your chart as a PDF (File → Save As / Export) and upload that instead. It takes seconds and parses better.`,
    };
  }
  if (!SUPPORTED_EXTENSIONS[ext]) {
    return {
      ok: false,
      reason: `Unsupported file type "${ext}". Upload a PDF, image (PNG/JPG), spreadsheet (XLSX/CSV), Word doc, or plain text file.`,
    };
  }
  if (file.size > MAX_SIZE_BYTES) {
    return { ok: false, reason: 'File is larger than 10 MB. Export a smaller version and try again.' };
  }
  return { ok: true, ext, contentType: SUPPORTED_EXTENSIONS[ext] };
}

/**
 * Upload the file, create the import doc, and fire WF7.
 * Returns the importId to subscribe to.
 */
export async function startOrgImport({ file, orgNameHint, user }) {
  if (!user?.uid) throw new Error('You must be signed in.');
  const check = validateImportFile(file);
  if (!check.ok) throw new Error(check.reason);

  const importId = `orgimp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const storagePath = `orgImports/${user.uid}/${importId}/${file.name}`;

  // 1. Upload to Storage
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, { contentType: check.contentType });
  const downloadURL = await getDownloadURL(fileRef);

  // 2. Create the import doc
  await setDoc(doc(db, COLLECTION_NAME, importId), {
    fileName: file.name,
    contentType: check.contentType,
    storagePath,
    orgNameHint: orgNameHint || null,
    requestedBy: user.uid,
    status: 'uploading',
    createdAt: Timestamp.now(),
  });

  // 3. Fire WF7 (fire-and-forget; the doc's status tells the UI what happened)
  triggerOrgImport({
    importId,
    downloadURL,
    fileName: file.name,
    contentType: check.contentType,
    orgNameHint: orgNameHint || null,
    requestedBy: user.uid,
    triggeredAt: new Date().toISOString(),
  });

  return importId;
}

/** Live subscription to an import doc. Returns the unsubscribe function. */
export function subscribeToOrgImport(importId, callback) {
  return onSnapshot(doc(db, COLLECTION_NAME, importId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

/**
 * Create the real organization from the reviewed structure.
 * `structure` is the (possibly user-edited) { orgName, industry, sites,
 * departments, teams } — ids are preserved so references stay intact.
 */
export async function approveOrgImport({ importId, structure, user, existingOrganization = null, includePeople = false }) {
  if (!user?.uid) throw new Error('You must be signed in.');
  if (!existingOrganization && !structure?.orgName?.trim()) throw new Error('Organization name is required.');
  if (!structure.sites?.length) throw new Error('At least one site is required.');

  // Extend mode (P4-14): add the reviewed structure to an EXISTING organization.
  if (existingOrganization) {
    const merged = mergeIntoExisting(existingOrganization, structure);
    const updates = {
      sites: merged.sites,
      departments: merged.departments,
      teams: merged.teams,
      ...(merged.divisions.length ? { divisions: merged.divisions } : {}),
      lastImportId: importId,
    };
    if (includePeople && structure.people?.length) {
      updates.roster = [...(existingOrganization.roster || []), ...rosterFromPeople(structure.people, { sites: merged.sites, departments: merged.departments, teams: merged.teams })];
    }
    await updateOrganization(existingOrganization.id, updates);
    await updateDoc(doc(db, COLLECTION_NAME, importId), { status: 'created', createdOrganizationId: existingOrganization.id, extended: true, added: merged.added, approvedAt: Timestamp.now() });
    return { ...existingOrganization, ...updates, added: merged.added };
  }

  // Referential integrity after user edits (deleting a site cascades in the UI,
  // but verify before writing).
  const siteIds = new Set(structure.sites.map((s) => s.id));
  const deptIds = new Set(structure.departments.map((d) => d.id));
  const orphanDept = structure.departments.find((d) => !siteIds.has(d.siteId));
  const orphanTeam = structure.teams.find((t) => !deptIds.has(t.departmentId));
  if (orphanDept) throw new Error(`Department "${orphanDept.name}" references a removed site.`);
  if (orphanTeam) throw new Error(`Team "${orphanTeam.name}" references a removed department.`);

  // 1. Create the org shell (creator becomes admin)
  const org = await createOrganization({
    name: structure.orgName.trim(),
    industry: structure.industry || '',
    createdBy: user.uid,
  });

  // 2. Write the full structure in one update (preserves proposed ids); site codes (P4-19),
  //    divisions (P4-15), crew/team type (P2-38), and — when the admin chose it — the people
  //    found in the chart as roster entries (no accounts yet, D07.1 "names found?").
  const sites = withSiteCodes(structure.sites.map(({ id, name, location, region, divisionId, code }) => ({
    id, name, location: location || '', ...(region ? { region } : {}), ...(divisionId ? { divisionId } : {}), code: code || '',
  })));
  const departments = structure.departments.map(({ id, name, siteId }) => ({ id, name, siteId }));
  const teams = structure.teams.map(({ id, name, departmentId, nwtType }) => ({ id, name, departmentId, nwtType: nwtType || 'team' }));
  await updateOrganization(org.id, {
    sites,
    departments,
    teams,
    ...(structure.divisions?.length ? { divisions: structure.divisions.map(({ id, name }) => ({ id, name })) } : {}),
    ...(includePeople && structure.people?.length ? { roster: rosterFromPeople(structure.people, { sites, departments, teams }) } : {}),
    importedFrom: importId,
  });

  // 3. Mark the import consumed
  await updateDoc(doc(db, COLLECTION_NAME, importId), {
    status: 'created',
    createdOrganizationId: org.id,
    approvedAt: Timestamp.now(),
  });

  return org;
}
